import { defineNuxtPlugin } from "nuxt/app";
import type { $Fetch, FetchRequest } from "ofetch";
import { useTracking } from "@composables/useTracking";

type TrackingFetchOptions = {
	baseURL?: string;
	_trackingStart?: number;
	_trackingDone?: boolean;
};

type GlobalFetch = typeof globalThis extends { $fetch: infer F } ? F : $Fetch;

export default defineNuxtPlugin((nuxtApp) => {
	if (!import.meta.client) return;

	const nuxtAppWithFetch = nuxtApp as unknown as { $fetch: GlobalFetch };
	const baseFetch: GlobalFetch = globalFetch.$fetch ?? nuxtAppWithFetch.$fetch;

	const timedFetch = baseFetch.create({
		onRequest: ({ request, options }) => {
			const trackingOptions = options as TrackingFetchOptions;
			if (!isTrackedApiRequest(request)) return;

			trackingOptions._trackingStart = now();
		},

		onResponse: ({ options }) => {
			trackResponseDuration(options as TrackingFetchOptions);
		},

		onResponseError: ({ options }) => {
			trackResponseDuration(options as TrackingFetchOptions);
		}
	});

	globalFetch.$fetch = timedFetch;
	nuxtAppWithFetch.$fetch = timedFetch;
});


const now = (): number => (typeof performance === "undefined" ? Date.now() : performance.now());
const tracking = useTracking();
const globalFetch = globalThis as typeof globalThis & { $fetch?: GlobalFetch };

const toRequestUrl = (request: FetchRequest): string | null => {
	if (typeof request === "string") return request;

	if (request instanceof URL) return request.toString();

	return request.url;
};

const isTrackedApiRequest = (request: FetchRequest): boolean => {
	const requestUrl = toRequestUrl(request);
	if (!requestUrl) return false;

	try {
		const segments = requestUrl.split("/").filter(Boolean);
		const isAPICall = segments.includes("api");

		return isAPICall;
	} catch {
		return false;
	}
};

const trackResponseDuration = (options: TrackingFetchOptions): void => {
	if (options._trackingDone) return;

	const start = options._trackingStart;
	if (typeof start !== "number") return;

	options._trackingDone = true;
	const duration = Math.max(0, Math.round(now() - start));
	tracking.trackAPIResponseTime(duration);
};
